/**
 * RTL (Right-to-Left) utility functions
 * Handles detection and configuration for RTL languages
 */

/**
 * List of RTL language codes
 * Add more RTL languages as needed
 */
const RTL_LANGUAGES = [
  'he', // Hebrew
];

/**
 * Check if a language code is RTL
 * @param languageCode - Language code (e.g., 'he', 'ar', 'en')
 * @returns true if the language is RTL, false otherwise
 */
export const isRTL = (languageCode: string): boolean => {
  if (!languageCode) return false;
  
  // Normalize the language code (lowercase, extract base language)
  const normalizedCode = languageCode.toLowerCase().split('-')[0];
  
  return RTL_LANGUAGES.includes(normalizedCode);
};

/**
 * Get text direction based on language code
 * @param languageCode - Language code
 * @returns 'rtl' or 'ltr'
 */
export const getDirection = (languageCode: string): 'rtl' | 'ltr' => {
  return isRTL(languageCode) ? 'rtl' : 'ltr';
};

/**
 * Set document direction based on language
 * @param languageCode - Language code
 */
export const setDocumentDirection = (languageCode: string): void => {
  if (typeof document === 'undefined') return;
  
  const direction = getDirection(languageCode);
  document.documentElement.dir = direction;
  document.body.dir = direction;
  
  // Set lang attribute for better accessibility
  document.documentElement.lang = languageCode;
};

/**
 * Get appropriate text alignment for RTL/LTR
 * @param languageCode - Language code
 * @returns 'right' or 'left'
 */
export const getTextAlign = (languageCode: string): 'right' | 'left' => {
  return isRTL(languageCode) ? 'right' : 'left';
};

/**
 * Get opposite text alignment (useful for edge cases)
 * @param languageCode - Language code
 * @returns 'left' or 'right'
 */
export const getOppositeTextAlign = (languageCode: string): 'right' | 'left' => {
  return isRTL(languageCode) ? 'left' : 'right';
};

/**
 * Mirror margin/padding values for RTL
 * Converts values like '0 10px 0 20px' to '0 20px 0 10px' for RTL
 * @param value - Margin or padding value
 * @param languageCode - Language code
 * @returns Mirrored value for RTL, original for LTR
 */
export const mirrorSpacing = (value: string, languageCode: string): string => {
  if (!isRTL(languageCode)) return value;
  
  const parts = value.trim().split(/\s+/);
  
  if (parts.length === 4) {
    // top right bottom left -> top left bottom right
    return `${parts[0]} ${parts[3]} ${parts[2]} ${parts[1]}`;
  }
  
  return value;
};

/**
 * Get appropriate CSS property name for direction-aware properties
 * @param property - Base property (e.g., 'marginLeft')
 * @param languageCode - Language code
 * @returns Mirrored property for RTL
 */
export const getMirroredProperty = (
  property: string,
  languageCode: string
): string => {
  if (!isRTL(languageCode)) return property;
  
  const mirrorMap: Record<string, string> = {
    left: 'right',
    right: 'left',
    Left: 'Right',
    Right: 'Left',
  };
  
  let result = property;
  Object.entries(mirrorMap).forEach(([key, value]) => {
    result = result.replace(key, value);
  });
  
  return result;
};

/**
 * MUI sx styles to disable RTL and force LTR
 * Use this constant in the sx prop of MUI components (Box, TextField, etc.)
 * 
 * @example
 * <Box sx={DISABLE_RTL_STYLES}>
 *   <TextField sx={DISABLE_RTL_STYLES} />
 * </Box>
 */
export const DISABLE_RTL_STYLES = {
  direction: 'ltr !important' as const,
  // Target all MUI RTL classes with high specificity
  '&[class*="muirtl"]': {
    direction: 'ltr !important',
  },
  '& *': {
    direction: 'ltr !important',
  },
  '& *[class*="muirtl"]': {
    direction: 'ltr !important',
  },
  '& .MuiInputBase-root': {
    direction: 'ltr !important',
  },
  '& .MuiInputBase-root[class*="muirtl"]': {
    direction: 'ltr !important',
  },
  '& .MuiFormControl-root': {
    direction: 'ltr !important',
  },
  '& .MuiFormControl-root[class*="muirtl"]': {
    direction: 'ltr !important',
  },
  '& .MuiInputBase-input': {
    direction: 'ltr !important',
    textAlign: 'left !important',
  },
  '& .MuiInputBase-input[class*="muirtl"]': {
    direction: 'ltr !important',
    textAlign: 'left !important',
  },
  '& input': {
    direction: 'ltr !important',
    textAlign: 'left !important',
  },
  '& input[class*="muirtl"]': {
    direction: 'ltr !important',
    textAlign: 'left !important',
  },
  '& input::placeholder': {
    textAlign: 'left !important',
  },
  '& .MuiInputAdornment-root': {
    direction: 'ltr !important',
  },
  '& .MuiInputAdornment-root[class*="muirtl"]': {
    direction: 'ltr !important',
  },
  '& .MuiInputBase-inputAdornedEnd': {
    direction: 'ltr !important',
    textAlign: 'left !important',
  },
  '& .MuiInputBase-inputAdornedEnd[class*="muirtl"]': {
    direction: 'ltr !important',
    textAlign: 'left !important',
  },
  '& .MuiInputBase-inputAdornedStart': {
    direction: 'ltr !important',
    textAlign: 'left !important',
  },
  '& .MuiInputBase-inputAdornedStart[class*="muirtl"]': {
    direction: 'ltr !important',
    textAlign: 'left !important',
  },
  '& .MuiSelect-select': {
    direction: 'ltr !important',
    textAlign: 'left !important',
  },
  '& .MuiSelect-select[class*="muirtl"]': {
    direction: 'ltr !important',
    textAlign: 'left !important',
  },
  '& .MuiMenuItem-root': {
    direction: 'ltr !important',
    textAlign: 'left !important',
  },
  '& .MuiMenuItem-root[class*="muirtl"]': {
    direction: 'ltr !important',
    textAlign: 'left !important',
  },
  '& .MuiPaper-root': {
    direction: 'ltr !important',
  },
  '& .MuiPaper-root[class*="muirtl"]': {
    direction: 'ltr !important',
  },
  '& .MuiList-root': {
    direction: 'ltr !important',
  },
  '& .MuiList-root[class*="muirtl"]': {
    direction: 'ltr !important',
  },
} as const;

/**
 * CSS class name for disabling RTL styling
 * Add this class to elements that need LTR styling
 * Make sure to add corresponding CSS rules in your global CSS file
 */
export const DISABLE_RTL_CLASS = 'disable-rtl';

/**
 * HTML attributes to disable RTL and force LTR
 * Use this object to spread into component props
 * 
 * @example
 * <Box {...DISABLE_RTL_PROPS} sx={DISABLE_RTL_STYLES}>
 *   Content
 * </Box>
 */
export const DISABLE_RTL_PROPS = {
  dir: 'ltr' as const,
  className: DISABLE_RTL_CLASS,
  style: { direction: 'ltr' as const },
} as const;

/**
 * Get styles and props to disable RTL for a component
 * Returns both sx styles and HTML attributes
 * 
 * @param options - Configuration options
 * @param options.textAlign - Text alignment (default: 'left')
 * @param options.includeProps - Whether to include HTML props (default: true)
 * @returns Object with sx styles and props
 * 
 * @example
 * const ltrStyles = getDisableRTLStyles({ textAlign: 'center' });
 * <Box {...ltrStyles.props} sx={ltrStyles.sx}>
 *   <TextField sx={ltrStyles.sx} />
 * </Box>
 */
export const getDisableRTLStyles = (options?: {
  textAlign?: 'left' | 'center' | 'right';
  includeProps?: boolean;
}) => {
  const { textAlign = 'left', includeProps = true } = options || {};
  
  const sx = {
    ...DISABLE_RTL_STYLES,
    '& .MuiInputBase-input': {
      ...DISABLE_RTL_STYLES['& .MuiInputBase-input'],
      textAlign: `${textAlign} !important` as const,
    },
    '& input': {
      ...DISABLE_RTL_STYLES['& input'],
      textAlign: `${textAlign} !important` as const,
    },
    '& input::placeholder': {
      textAlign: `${textAlign} !important` as const,
    },
  };
  
  return {
    sx,
    props: includeProps ? DISABLE_RTL_PROPS : {},
    className: DISABLE_RTL_CLASS,
  };
};

